import Head from 'next/head';
import * as React from 'react';
import { PaginationEntity } from '../../../interfaces/explore/PaginationEntity';
import { GA4Tag } from 'src/components/utils/GTMUtils';
import { buildUrl } from 'src/utils/request-url';

interface Props {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  keywords?: string;
  currentPath: string;
  pagination?: PaginationEntity;
  noIndex: boolean;
  opgDisabled?: boolean;
  googleAnalyticsFourTrackingId?: string;
  faviconImageUrl?: string;
}

const MetaTag = (initProps: Props) => {
  const defaultProps = {
    title: 'かんたん予約',
    keywords: '予約, クービック, かんたん予約, ストアーズ, STORES',
    noIndex: false,
    opgDisabled: false,
    faviconImageUrl: buildUrl('/static/favicon.ico'),
  } as const;

  // undefinedのプロパティを削除して上書きしないようにする
  const filteredInitProps = Object.fromEntries(
    Object.entries(initProps).filter(([_, v]) => v !== undefined),
  ) as Props;

  const {
    title,
    description,
    thumbnailUrl,
    keywords,
    currentPath,
    pagination,
    noIndex,
    opgDisabled,
    googleAnalyticsFourTrackingId,
    faviconImageUrl,
  } = {
    ...defaultProps,
    ...filteredInitProps,
  };

  const currentUrl = buildUrl(currentPath, 'absolute');
  const canonicalUrl = currentUrl
    .replace(/\?page=1$/g, '')
    .replace(/\/\?/g, '?')
    .replace(/\/$/, '');
  const baseUrl = currentUrl.replace(/\?.*$/, '');
  const robotsContent = noIndex ? 'noindex, follow, noarchive' : 'noarchive';

  const escapedDescription = description?.replace(/\s+/g, ' ');

  return (
    <>
      {googleAnalyticsFourTrackingId && (
        <GA4Tag
          googleAnalyticsFourTrackingId={googleAnalyticsFourTrackingId}
          pageTitle={title}
        />
      )}
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <title>{title}</title>
        <meta content={robotsContent} name="robots" />
        <meta name="keywords" content={keywords} />
        <meta name="description" content={escapedDescription} />
        {!opgDisabled && (
          <>
            <meta property="og:site_name" content={title} />
            <meta property="og:type" content="website" />
            <meta property="og:description" content={escapedDescription} />
            <meta property="og:image" content={thumbnailUrl} />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={escapedDescription} />
            <meta name="twitter:image" content={thumbnailUrl} />
          </>
        )}
        <link href={canonicalUrl} rel="canonical" />
        <link rel="icon" type="imae/x-icon" href={faviconImageUrl} />
        <link rel="shortcut icon" href={faviconImageUrl} />
        {pagination && pagination.currentPage < pagination.totalPages && (
          <link
            rel="next"
            href={`${baseUrl}?page=${pagination.currentPage + 1}`}
          />
        )}
        {pagination && pagination.currentPage !== 1 && (
          <link
            rel="prev"
            href={`${baseUrl}?page=${pagination.currentPage - 1}`}
          />
        )}
      </Head>
    </>
  );
};

export default MetaTag;
